import React, { FC, useEffect, useMemo, useRef } from 'react';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { useTranslation } from 'react-i18next';
import { useMatch } from 'react-router-dom';
import { useTheme } from '@mui/material';
import Styles from '@/assets/js/Styles';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { includeInPageIncludingFolder, includePage } from '@/utils/functions';
import { PageFolder } from '@/declarations/models/Folder';
import { FolderView } from '@/views/ContentView/PageTree/FolderView';
import { Page } from '@/declarations/models/Page';
import { ancestorIds } from '@/views/ContentView/lastPage';
import { scrollIntoViewWhenSettled } from '@/views/ContentView/PageTree/scrollIntoViewWhenSettled';
import M24PageFinderPageTreeContent from './M24PageFinderPageTreeContent';
import { useStore } from '../../../store/Store';
import { useM24PageFinderContentView } from '../M24PageFinder';
import Container from '../../../Container';

interface M24PageFinderPageTreeProps {
  isEvent?: boolean;
  frontpage?: Page;
  pageFolders: PageFolder[];
  selectedPageFolder: PageFolder | null;
  setSelectedPageFolder: (folder: PageFolder | null) => void;
  folderBreadcrumbs: Array<PageFolder>;
}

const CollapseIcon = () => <MaterialSymbol name='expand_more' />;
const ExpandIcon = () => <MaterialSymbol name='chevron_right' />;

const M24PageFinderPageTree: FC<M24PageFinderPageTreeProps> = ({
  isEvent,
  frontpage,
  pageFolders,
  selectedPageFolder,
  setSelectedPageFolder,
  folderBreadcrumbs,
}) => {
  const { t } = useTranslation('aria');
  const { state } = useStore();
  const theme = useTheme();

  const { expandedPageIds, setExpandedPageIds, selectionState, filterValues, hasFilterApplied, sortFn, pages } =
    useM24PageFinderContentView();

  const { getRootNodes, getChildren, hasChildren, itemCount } = selectionState;

  // The page open in the editor shows: its ancestors open, and the page itself when it has children
  const currentPageId = Number(useMatch('/editor/:modelId')?.params.modelId) || null;
  const currentPageExpandedIds = useMemo<Array<string>>(() => {
    if (!currentPageId || !pages.some((page) => page.id === currentPageId)) return [];
    const ids = ancestorIds(pages, currentPageId);
    return hasChildren(currentPageId) ? [String(currentPageId), ...ids] : ids;
  }, [currentPageId, pages, hasChildren]);

  // Opened from a page in the editor, only the way down to it; otherwise every top page, when there are 5 at most
  const defaultExpandedIds = useMemo<Array<string>>(() => {
    if (currentPageExpandedIds.length) return currentPageExpandedIds;
    const roots = getRootNodes(frontpage?.id).map((n) => String(n.id || ''));
    return roots.length <= 5 ? roots : [];
  }, [frontpage?.id, getRootNodes, currentPageExpandedIds]);

  const visibleNodeIds = useMemo<null | Array<string | number>>(() => {
    if (!hasFilterApplied) {
      return null;
    }

    const getVisibleNodeIds = (page: Page): Array<string | number> => {
      const key = page.id || 0;

      if (hasChildren(key)) {
        const visibleChildren = getChildren(key).reduce<Array<string | number>>((ids, child) => {
          ids.push(...getVisibleNodeIds(child).filter((id) => !ids.includes(id)));
          return ids;
        }, []);

        if (visibleChildren.length > 0) {
          visibleChildren.push(key);
          return visibleChildren;
        }
      }

      return includePage(filterValues, page) ? [key] : [];
    };

    return getRootNodes(frontpage?.id).reduce<Array<number | string>>((ids, root) => {
      ids.push(...getVisibleNodeIds(root));
      return ids;
    }, []);
  }, [hasFilterApplied, getRootNodes, frontpage?.id, hasChildren, filterValues, getChildren]);

  useEffect(() => {
    if (hasFilterApplied && !!visibleNodeIds?.length) {
      setExpandedPageIds(visibleNodeIds.map(String));
    } else {
      setExpandedPageIds(defaultExpandedIds);
    }
  }, [hasFilterApplied, defaultExpandedIds, setExpandedPageIds, visibleNodeIds]);

  // Scroll to the row of the page open in the editor, once per opening of the finder
  const scrolledToCurrentPage = useRef(false);
  const cancelScroll = useRef<() => void>(undefined);
  useEffect(() => () => cancelScroll.current?.(), []);
  useEffect(() => {
    if (scrolledToCurrentPage.current || hasFilterApplied || !currentPageExpandedIds.length) return;
    scrolledToCurrentPage.current = true;
    cancelScroll.current = scrollIntoViewWhenSettled(`[data-page-finder-id="${currentPageId}"] > .MuiTreeItem-content`);
  }, [currentPageId, currentPageExpandedIds, hasFilterApplied]);

  const selectedOutline = `2px solid ${Styles.Colors.STRONG_GREEN}`;

  const renderNode = (page: Page) => {
    const pageId = page.id || 0;
    const childNodes = getChildren(pageId).sort(sortFn);

    if (visibleNodeIds && !visibleNodeIds.includes(pageId)) {
      return null;
    }

    const selected = selectionState.isSelected(pageId);

    return (
      <TreeItem
        key={pageId}
        itemId={String(pageId)}
        data-page-finder-id={pageId}
        slotProps={{
          content: {
            style: {
              outline: selected ? selectedOutline : undefined,
              margin: '6px 0',
              // The node opened for the page in the editor, shaded like a hovered row
              backgroundColor: String(pageId) === currentPageExpandedIds[0] ? theme.palette.action.hover : undefined,
            },
          },
        }}
        label={
          <M24PageFinderPageTreeContent
            page={page}
            diffuse={hasFilterApplied && !includePage(filterValues, page)}
            selected={selected}
          />
        }>
        {hasChildren(pageId) ? childNodes.map(renderNode) : null}
      </TreeItem>
    );
  };

  if (!itemCount) {
    return null;
  }

  return (
    <>
      {hasFilterApplied || isEvent ? (
        <Container fullWidth column gap={1}>
          {isEvent && pageFolders.length > 0 && (
            <FolderView
              siteFolders={pageFolders}
              activePageFolder={selectedPageFolder}
              onFolderClick={setSelectedPageFolder}
              breadcrumbs={folderBreadcrumbs}
            />
          )}

          {pages
            ?.sort(sortFn)
            .filter((p) => includeInPageIncludingFolder(filterValues, p, selectedPageFolder))
            .map((page) => {
              const pageId = page.id ?? 0;
              const selected = selectionState.isSelected(pageId);

              return (
                <Container
                  key={pageId}
                  fullWidth
                  sx={{
                    outline: selected ? selectedOutline : undefined,
                    px: 2,
                    backgroundColor: !isEvent ? Styles.Colors.LIGHT_GREY : Styles.Colors.STRONG_GREEN_TRANSPARENT,
                  }}>
                  <M24PageFinderPageTreeContent
                    page={page}
                    diffuse={hasFilterApplied && !includePage(filterValues, page)}
                    selected={selected}
                  />
                </Container>
              );
            })}
        </Container>
      ) : (
        <>
          {frontpage && (
            <Container
              fullWidth
              sx={{
                outline: selectionState.isSelected(frontpage.id!) ? selectedOutline : undefined,
                px: 2,
                backgroundColor: Styles.Colors.LIGHT_BLUE,
              }}>
              <M24PageFinderPageTreeContent page={frontpage} selected={selectionState.isSelected(frontpage.id!)} />
            </Container>
          )}

          <SimpleTreeView
            sx={{ width: '100%' }}
            id='page-tree'
            aria-label={t('components.PageTree.PageTreeDescription', {
              siteName: state.selectedSite?.name || 'unknown',
            })}
            expandedItems={expandedPageIds}
            onExpandedItemsChange={(_, ex) => setExpandedPageIds(ex)}
            slots={{
              collapseIcon: CollapseIcon,
              expandIcon: ExpandIcon,
            }}
            disableSelection>
            {getRootNodes(frontpage?.id)
              .filter((p) => !frontpage || p.id !== frontpage.id)
              .sort(sortFn)
              .map(renderNode)}
          </SimpleTreeView>
        </>
      )}
    </>
  );
};

export default M24PageFinderPageTree;
